import { t } from "@lingui/core/macro";
import type {
  DeleteStaffTimeOffsBulk,
  DeleteTimeOffAbsences,
  DeleteTimeOffOccurrence,
  GetStaffTimeOffList,
  GetTimeOffAbsenceDays,
  GetTimeOffAbsenceWindow,
  GetTimeOffAbsences,
  GetTimeOff,
  GetTimeOffOccurrence,
  GetTimeOffShiftDates,
  PostTimeOff,
  PostTimeOffDryRun,
  PostTimeOffOverShifts,
  PostTimeOffOverShiftsDryRun,
  PutTimeOffOccurrence,
  PutTimeOffOverShifts,
  PutTimeOffOverShiftsDryRun,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { keepPreviousData } from "@tanstack/react-query";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";
import { sentryLog } from "@/utils/sentry-utils";

/** GET /calendar/timeoff */
export const useGetTimeoff = (
  query: GetTimeOff["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["timeoff", query],
    queryFn: () => get<GetTimeOff>("/calendar/timeoff", query),
    enabled: enabled ?? true,
  });
};

/** POST /time-off: one action across many staff and resources, every row sharing one batchId. */
export const useCreateTimeOff = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationFn: (body: PostTimeOff["body"]) =>
      post<PostTimeOff>("/time-off", body),
    onSuccess: options?.onSuccess,
    displayNotification: options?.displayNotification ?? true,
  });
};

/** POST /time-off dry run; `body` is the serialised create body, so a debounced string keys the query. */
export const usePreviewTimeOff = (body: string | null) => {
  return useCustomQuery({
    queryKey: ["time-off-preview", body],
    queryFn: () =>
      post<PostTimeOffDryRun>("/time-off", {
        ...(JSON.parse(body as string) as PostTimeOff["body"]),
        dryRun: true,
      }),
    enabled: body != null,
    retry: false,
    placeholderData: keepPreviousData,
  });
};

/** GET /time-off/:target/:id: one occurrence with its series rule, absence range and summary. */
export const useGetTimeOffOccurrence = (
  params: { target: "staff" | "resource"; id: number } | null,
) => {
  return useCustomQuery({
    queryKey: ["timeoff", "occurrence", params],
    queryFn: () =>
      get<GetTimeOffOccurrence>(
        `/time-off/${params?.target ?? "staff"}/${params?.id ?? 0}`,
      ),
    enabled: params != null,
  });
};

/** GET /time-off/shift-dates: the days any of the people is rostered on. */
export const useGetTimeOffShiftDates = (
  query: GetTimeOffShiftDates["query"] | null,
) => {
  return useCustomQuery({
    queryKey: ["timeoff", "shift-dates", query],
    queryFn: () =>
      get<GetTimeOffShiftDates>(
        "/time-off/shift-dates",
        query as GetTimeOffShiftDates["query"],
      ),
    enabled: query != null,
    placeholderData: keepPreviousData,
  });
};

/** PUT /time-off/:target/:id: `scope` detaches this occurrence or rewrites the rest of its series. */
export const useUpdateTimeOffOccurrence = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationFn: ({
      target,
      id,
      ...body
    }: PutTimeOffOccurrence["body"] & {
      target: "staff" | "resource";
      id: number;
    }) => put<PutTimeOffOccurrence>(`/time-off/${target}/${id}`, body),
    onSuccess: options?.onSuccess,
    displayNotification: options?.displayNotification ?? true,
  });
};

/** DELETE /time-off/:target/:id */
export const useDeleteTimeOffOccurrence = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationFn: ({
      target,
      id,
      ...body
    }: DeleteTimeOffOccurrence["body"] & {
      target: "staff" | "resource";
      id: number;
    }) => del<DeleteTimeOffOccurrence>(`/time-off/${target}/${id}`, body),
    onSuccess: options?.onSuccess,
    displayNotification: options?.displayNotification ?? true,
  });
};

/** GET /time-off/list: the absences tab's paginated day-level `StaffTimeOff` listing. */
export const useGetStaffTimeOffList = (query: GetStaffTimeOffList["query"]) => {
  return useCustomQuery({
    queryKey: ["staff-timeoff-list", query],
    queryFn: () => get<GetStaffTimeOffList>("/time-off/list", query),
    placeholderData: keepPreviousData,
  });
};

/** GET /time-off/absences: one item per person's absence, paginated by absence. */
export const useGetTimeOffAbsences = (
  query: GetTimeOffAbsences["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["timeoff", "absences", query],
    queryFn: () => get<GetTimeOffAbsences>("/time-off/absences", query),
    placeholderData: keepPreviousData,
    enabled: enabled ?? true,
  });
};

class AbsenceWindowResponseError extends Error {}

/** GET /time-off/absences with a window: every absence in it with its days, and the staff with their shift days. */
export const useGetTimeOffAbsenceWindow = (
  query: GetTimeOffAbsenceWindow["query"],
  enabled?: boolean,
) => {
  return useCustomQuery({
    queryKey: ["timeoff", "absence-window", query],
    queryFn: async () => {
      const response = await get<GetTimeOffAbsenceWindow>(
        "/time-off/absences",
        query,
      );
      // A server on a release before the window ignores `windowFrom` and answers with the paginated list.
      if (!Array.isArray(response.staff)) {
        sentryLog.warn("Absence window response has no staff", {
          windowFrom: query.windowFrom,
          windowTo: query.windowTo,
        });
        throw new AbsenceWindowResponseError(t`Prišlo je do sistemske napake`);
      }
      return response;
    },
    placeholderData: keepPreviousData,
    enabled: enabled ?? true,
  });
};

/** GET /time-off/absences/days: every day of one absence, by the key the list gave it. */
export const useGetTimeOffAbsenceDays = (key: string | null) => {
  return useCustomQuery({
    queryKey: ["timeoff", "absence-days", key],
    queryFn: () =>
      get<GetTimeOffAbsenceDays>("/time-off/absences/days", {
        key: key ?? "",
      }),
    enabled: key != null,
  });
};

/** DELETE /time-off/absences: each occurrence's whole absence, or its days from `fromDate`. */
export const useDeleteTimeOffAbsences = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationFn: (body: DeleteTimeOffAbsences["body"]) =>
      del<DeleteTimeOffAbsences>("/time-off/absences", body),
    onSuccess: options?.onSuccess,
    displayNotification: options?.displayNotification ?? true,
  });
};

/** DELETE /time-off/bulk: removes the selected occurrences, leaving their series alone. */
export const useDeleteStaffTimeOffsBulk = (options?: {
  onSuccess?: () => void;
}) => {
  return useCustomMutation({
    mutationFn: (body: DeleteStaffTimeOffsBulk["body"]) =>
      del<DeleteStaffTimeOffsBulk>("/time-off/bulk", body),
    onSuccess: options?.onSuccess,
    displayNotification: true,
  });
};

/** POST /time-off/over-shifts: an absence carved out of the rota, one row per shift. */
export const useCreateTimeOffOverShifts = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationFn: (body: PostTimeOffOverShifts["body"]) =>
      post<PostTimeOffOverShifts>("/time-off/over-shifts", body),
    onSuccess: options?.onSuccess,
    displayNotification: options?.displayNotification ?? true,
  });
};

/** POST /time-off/over-shifts dry run: what an absence would write per person. */
export const usePreviewTimeOffOverShifts = (body: string | null) => {
  return useCustomQuery({
    queryKey: ["time-off-preview", "over-shifts", body],
    queryFn: () =>
      post<PostTimeOffOverShiftsDryRun>("/time-off/over-shifts", {
        ...(JSON.parse(body as string) as PostTimeOffOverShifts["body"]),
        dryRun: true,
      }),
    enabled: body != null,
    retry: false,
    placeholderData: keepPreviousData,
  });
};

/** PUT /time-off/over-shifts/:id: re-saves an absence from its first not-yet-started day on. */
export const useResaveTimeOffOverShifts = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    mutationFn: ({
      id,
      ...body
    }: PutTimeOffOverShifts["body"] & { id: number }) =>
      put<PutTimeOffOverShifts>(`/time-off/over-shifts/${id}`, body),
    onSuccess: options?.onSuccess,
    displayNotification: options?.displayNotification ?? true,
  });
};

/** PUT /time-off/over-shifts/:id dry run */
export const usePreviewTimeOffOverShiftsResave = (
  id: number | null,
  body: string | null,
) => {
  return useCustomQuery({
    queryKey: ["time-off-preview", "over-shifts", id, body],
    queryFn: () =>
      put<PutTimeOffOverShiftsDryRun>(`/time-off/over-shifts/${id ?? 0}`, {
        ...(JSON.parse(body as string) as PutTimeOffOverShifts["body"]),
        dryRun: true,
      }),
    enabled: id != null && body != null,
    retry: false,
    placeholderData: keepPreviousData,
  });
};
